import { searchProviderLabel } from '../../../api/src/search/search-provider.types';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api/client';
import { sectionById } from '../api/generated/section-catalog';
import type { FakesSearchProfile } from '../api/types';
import type { FakesCollectionSettingsView } from '../../../api/src/fakes/fakes-collection.types';
import type { DailyScheduleSettings } from '../../../api/src/common/daily-schedule.types';
import { DailyScheduleFields } from '../components/DailyScheduleFields';
import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { Stack, Typography } from '../components/mui-system';
import { fakeError, fakeDate } from '../components/fakes-presentation';
import { useWorkspace } from '../workspace-context';

const fields = (s: DailyScheduleSettings): DailyScheduleSettings => ({
  revision: s.revision,
  enabled: s.enabled,
  collectTime: s.collectTime,
  timeZone: s.timeZone,
});
const states = {
  pending: 'Ожидает запуска',
  started: 'Сбор запущен',
  failed: 'Не запущен',
  cancelled: 'Отменён',
};
export function FakesCollectionDialog({
  workspace,
  groupId = workspace,
  groupName = 'Основная',
  count,
  profile,
  yandexSyncFallbackOn429 = false,
  onClose,
}: {
  workspace: string;
  groupId?: string;
  groupName?: string;
  count: number;
  profile: FakesSearchProfile | null;
  yandexSyncFallbackOn429?: boolean;
  onClose(): void;
}) {
  const editable = useCanEdit('fakes.schedule', 'fakes.run'),
    { user } = useWorkspace(),
    navigate = useNavigate();
  const [saved, setSaved] = useState<FakesCollectionSettingsView | null>(null),
    [draft, setDraft] = useState<DailyScheduleSettings | null>(null);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [confirmation, setConfirmation] = useState(false);
  const live = useRef(true),
    locked = useRef(false);
  useEffect(() => {
    live.current = true;
    void api
      .fakesCollectionSettings(workspace, groupId)
      .then((s) => {
        if (live.current) {
          setSaved(s);
          setDraft(fields(s));
        }
      })
      .catch((e) => {
        if (live.current) setError(fakeError(e));
      });
    return () => {
      live.current = false;
    };
  }, [workspace, groupId]);
  async function save() {
    if (!draft || locked.current) return;
    locked.current = true;
    setBusy(true);
    setError('');
    try {
      const s = await api.saveFakesCollectionSettings(workspace, draft, groupId);
      if (live.current) {
        setSaved(s);
        setDraft(fields(s));
        setConfirmation(false);
        setNotice('Расписание выдачи сохранено.');
      }
    } catch (e) {
      if (live.current) setError(fakeError(e));
    } finally {
      locked.current = false;
      if (live.current) setBusy(false);
    }
  }
  const dirty = Boolean(saved && draft && JSON.stringify(fields(saved)) !== JSON.stringify(draft));
  return (
    <>
      <Dialog open fullWidth maxWidth="sm" onClose={() => !busy && onClose()}>
        <DialogTitle>Расписание сбора выдачи · {groupName}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="success">{notice}</Alert>}
            {!draft || !saved ? (
              !error && <CircularProgress aria-label="Загрузка расписания выдачи" />
            ) : (
              <>
                <Typography color="text.secondary">
                  Выбранный провайдер собирает активные запросы этой группы по её профилю, даже если
                  Telegram-отчёты выключены. У каждой группы своё расписание.
                </Typography>
                <DailyScheduleFields
                  value={draft}
                  onChange={setDraft}
                  disabled={busy || !editable}
                  label="Собирать выдачу ежедневно"
                  timeLabel="Время сбора выдачи"
                />
                <Alert severity="info">
                  Сейчас запросов: {count}.{' '}
                  {profile
                    ? `${searchProviderLabel(profile)} · ${profile.searchEngine === 'yandex' ? 'Яндекс' : 'Google'} · ${profile.regionName} · ${profile.device === 'desktop' ? 'Desktop' : 'Mobile'} · Top-${profile.depth}.`
                    : 'Профиль группы ещё не выбран.'}{' '}
                  Каждый сбор расходует баланс выбранного провайдера.
                </Alert>
                <Typography variant="body2">
                  Следующий сбор:{' '}
                  {saved.nextRunAt
                    ? `${new Date(saved.nextRunAt).toLocaleString('ru-RU', { timeZone: saved.timeZone })} (${saved.timeZone})`
                    : 'Выключен'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Если время уже прошло, свободный дневной запуск начнётся в ближайшую минуту. Один
                  запуск на местную дату; пропущенные прошлые дни не собираются. Остановка
                  расписания не прерывает уже начатый сбор.
                </Typography>
                <Divider />
                <Typography variant="h4">Реестр наших доменов</Typography>
                <Typography variant="body2">
                  Обновление n8n имеет своё глобальное расписание. Сбор выдачи и отчёты используют
                  последнюю сохранённую версию и показывают её свежесть.
                </Typography>
                {user.isAdmin && (
                  <Button
                    onClick={() => {
                      onClose();
                      navigate('/admin/n8n-domains');
                    }}
                  >
                    Настроить обновление n8n
                  </Button>
                )}
                <Divider />
                <Typography variant="h4">Последние дневные запуски</Typography>
                {!saved.recent.length && (
                  <Typography color="text.secondary">
                    Запусков по расписанию ещё не было.
                  </Typography>
                )}
                {saved.recent.map((row) => (
                  <Stack key={row.id} gap={0.5}>
                    <Typography>
                      {row.day} · {states[row.state]} · {fakeDate(row.createdAt)}
                    </Typography>
                    {row.error && (
                      <Typography color="error.main" variant="body2">
                        {row.error}
                      </Typography>
                    )}
                    {row.runId && (
                      <Button
                        size="small"
                        onClick={() => {
                          onClose();
                          navigate(
                            `${sectionById('fakes').path}?run=${row.runId}&group=${encodeURIComponent(groupId)}`,
                          );
                        }}
                      >
                        Открыть запуск
                      </Button>
                    )}
                  </Stack>
                ))}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onClose}>
            Закрыть
          </Button>
          <EditorOnly permissions={['fakes.schedule', 'fakes.run']}>
            <Button
              variant="contained"
              disabled={busy || !draft || !dirty}
              onClick={() => (draft?.enabled ? setConfirmation(true) : void save())}
            >
              Сохранить расписание
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmation}
        fullWidth
        maxWidth="sm"
        onClose={() => !busy && setConfirmation(false)}
      >
        <DialogTitle>Включить ежедневный сбор выдачи?</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>
              Ежедневно в {draft?.collectTime} ({draft?.timeZone}) будут собираться активные запросы
              группы «{groupName}». Сейчас запросов: {count}, глубина: Top-{profile?.depth ?? '—'}.
              Сбор расходует баланс выбранного провайдера и работает независимо от отправки отчётов.
            </Typography>
            {profile && <Typography fontWeight={650}>{searchProviderLabel(profile)}</Typography>}
            {yandexSyncFallbackOn429 && (
              <Alert severity="warning">
                При HTTP 429 отложенного запроса ежедневный сбор использует одну более дорогую
                синхронную попытку.
              </Alert>
            )}
            {profile?.provider === 'yandex_search_api' && profile.yandexSearchMode === 'sync' && (
              <Alert severity="warning">
                Ежедневный сбор будет использовать более дорогой синхронный режим Yandex Search API.
              </Alert>
            )}
            <Typography>
              Если время сегодня уже прошло и дневной запуск ещё не занят, сбор начнётся в ближайшую
              минуту.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(false)}>
            Отмена
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => void save()}>
            Подтвердить сбор
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
